import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useImageAttachmentMutation } from '../../../../queries/Attachments/useImageAttachmentMutation';
import { invalidateAccountLoad, useAccountLoad, useAdminAccountQuery, useAttachmentQuery } from '../../../../queries';
import { useGetTestimonialTemplate } from '../../../../queries/GoogleBusiness/useGetTestimonialTemplate';
import { useGetTestimonialTemplateFormats } from '../../../../queries/GoogleBusiness/useGetTestimonialTemplateFormats';
import { useSetGoogleBusinessBrandingSettings } from '../../../../queries/GoogleBusiness/useSetGoogleBusinessBrandingSettings';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import { ColorPicker } from '../../../components-v2/ColorPicker';
import { ImageCropDialog } from '../../../components-v2/ImageCropDialog';
import { TabToggle } from '../../../components-v2/TabToggle';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { baseStyleSheet } from '../../../styles/styles';
import { DEFAULT_COLORS, LAYOUT_PREVIEW_SAMPLE } from '../../TestimonialImageEditor/constants';
import { FontSizeInput } from '../../TestimonialImageEditor/FontSizeInput';
import { useGetImageUrl } from '../../TestimonialImageEditor/hooks/useGetImageUrl';
import { buildTestimonialPreview } from '../../TestimonialImageEditor/interpolateTestimonialTemplate';
import { resolveLayout } from '../../TestimonialImageEditor/layoutBoxGeometry';
import { TestimonialCardCanvas } from '../../TestimonialImageEditor/TestimonialCardCanvas';
import { LayoutElement } from '../../TestimonialImageEditor/types';
import {
	addColorOverlayToImage,
	TESTIMONIAL_OVERLAY_COLOR,
	TESTIMONIAL_OVERLAY_OPACITY,
} from '../../TestimonialImageEditor/utils';
import { styleSheet } from '../styles';

const shapeOf = (canvas: Api.IGoogleReviewPostCanvas) => {
	if (canvas.width === canvas.height) {
		return 'Square';
	}
	return canvas.width > canvas.height ? 'Landscape' : 'Portrait';
};

const IMAGES_FOR_SELECTION = [
	{ imagePath: '/static/images/googleReview/bitmap-1.png', slug: 'testimonial-1' },
	{ imagePath: '/static/images/googleReview/bitmap-2.png', slug: 'testimonial-2' },
	{ imagePath: '/static/images/googleReview/bitmap-3.png', slug: 'testimonial-3' },
];

type GoogleReviewBrandingFormValues = {
	backgroundColor: string;
	foregroundColor: string;
	includeGoogleLogo: boolean;
	includeStars: boolean;
	image?: Api.IFileAttachment;
	layout?: Api.IGoogleReviewPostLayout;
	templateKey?: string;
	templateRevision?: number;
};

const getDefaultFormValues = (branding?: Api.IGoogleBusinessBrandingSettings): GoogleReviewBrandingFormValues => ({
	backgroundColor: branding?.backgroundColor ?? DEFAULT_COLORS.background,
	foregroundColor: branding?.foregroundColor ?? DEFAULT_COLORS.text,
	includeGoogleLogo: branding?.includeGoogleLogo ?? true,
	includeStars: branding?.includeStars ?? true,
	image: branding?.image,
	layout: branding?.layout,
	templateKey: branding?.templateKey,
	templateRevision: branding?.templateRevision,
});

interface IGoogleReviewBrandingSectionProps {
	/** Optional impersonation context. If not provided, uses the current user session's context. */
	impersonationContext?: Api.IImpersonationContext;
}

export const GoogleReviewBrandingSection: React.FC<IGoogleReviewBrandingSectionProps> = ({
	impersonationContext: propImpersonationContext,
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const impersonationContext = propImpersonationContext ?? userSession.impersonationContext;
	const account = impersonationContext?.account ?? userSession.account;

	const { data: accountDataUser } = useAccountLoad({
		accountId: account?.id ?? '',
		enabled: !impersonationContext,
	});

	const { data: accountDataAdmin } = useAdminAccountQuery({
		accountId: account?.id ?? '',
		enabled: Boolean(impersonationContext),
	});

	const accountData = impersonationContext ? accountDataAdmin : accountDataUser;

	const branding = accountData?.preferences?.googleReviewPostBranding;
	const defaultValues = React.useMemo(() => getDefaultFormValues(branding), [branding]);

	const { handleSubmit, watch, setValue, getValues, reset, formState } = useForm<GoogleReviewBrandingFormValues>({
		defaultValues,
	});

	const { isDirty } = formState;
	const backgroundColor = watch('backgroundColor');
	const foregroundColor = watch('foregroundColor');
	const includeGoogleLogo = watch('includeGoogleLogo');
	const includeStars = watch('includeStars');
	const image = watch('image');
	const layout = watch('layout');
	const templateKey = watch('templateKey');
	const templateRevision = watch('templateRevision');

	const imageId = image?.id;
	const { data: imageData } = useAttachmentQuery({
		id: imageId ?? '',
		enabled: !!imageId,
		impersonationContext: impersonationContext
			? {
					account: { id: impersonationContext.account?.id },
					user: impersonationContext?.user ? { id: impersonationContext.user?.id } : undefined,
				}
			: undefined,
	});

	const [backgroundImageModalOpen, setBackgroundImageModalOpen] = React.useState(false);
	const [cropModalOpen, setCropModalOpen] = React.useState(false);
	const [pendingCropImageUrl, setPendingCropImageUrl] = React.useState<string | null>(null);
	const [cropSource, setCropSource] = React.useState<{ imageId: string; url: string } | null>(null);

	const { getImageUrl } = useGetImageUrl(impersonationContext);

	const { data: formats, isPending: formatsPending } = useGetTestimonialTemplateFormats({ impersonationContext });

	const [pickedFormat, setPickedFormat] = React.useState<{ accountId?: string; key: string } | null>(null);
	const pickedFormatKey = pickedFormat?.accountId === account?.id ? pickedFormat?.key : undefined;

	const storedFormatKey = branding?.templateKey;
	const accountFormatKey = storedFormatKey ?? formats?.find(format => format.isDefault)?.key ?? formats?.[0]?.key;
	const activeFormatKey = pickedFormatKey ?? accountFormatKey;

	const { data: template } = useGetTestimonialTemplate({
		impersonationContext,
		key: activeFormatKey,
		enabled: !formatsPending,
	});
	const canvas = template?.canvas;
	const templateIsForActiveFormat = !!template && (!activeFormatKey || template.key === activeFormatKey);

	const storedFormatShape = formats?.find(format => format.key === storedFormatKey)?.canvas;

	const cropIsForAnotherFormat =
		!!storedFormatKey &&
		!!activeFormatKey &&
		activeFormatKey !== storedFormatKey &&
		!!image?.id &&
		image.id === branding?.image?.id;

	const sourceImageUrl = image?.id && cropSource?.imageId === image.id ? cropSource.url : null;
	const canRecropForThisFormat = !cropIsForAnotherFormat || !!sourceImageUrl;

	const formatIsDirty = !!pickedFormatKey && pickedFormatKey !== storedFormatKey;
	const hasUnsavedChanges = isDirty || formatIsDirty;

	const [selectedElement, setSelectedElement] = React.useState<LayoutElement>('text');
	const backgroundImageUrl = image?.url ?? imageData?.url ?? null;

	const backgroundAttachment = image ? { ...image, url: backgroundImageUrl ?? undefined } : undefined;

	const canEditBackgroundWithAI = !!image?.id && !!backgroundImageUrl;

	const resolvedLayout = React.useMemo(
		() => (template ? resolveLayout(template, layout, { key: templateKey, revision: templateRevision }, null) : null),
		[template, layout, templateKey, templateRevision]
	);

	const { previewHtml, previewError } = React.useMemo(
		() =>
			buildTestimonialPreview(template, {
				backgroundColor,
				foregroundColor,
				backgroundImage: backgroundImageUrl,
				reviewText: LAYOUT_PREVIEW_SAMPLE.reviewText,
				authorName: LAYOUT_PREVIEW_SAMPLE.authorName,
				rating: LAYOUT_PREVIEW_SAMPLE.rating,
				includeGoogleLogo,
				includeStars,
				includeAuthor: true,
				source: 'GoogleReview',
				layout: resolvedLayout,
			}),
		[template, backgroundColor, foregroundColor, backgroundImageUrl, includeGoogleLogo, includeStars, resolvedLayout]
	);

	const visibleElements: LayoutElement[] = [
		'text',
		...(includeGoogleLogo ? (['logo'] as LayoutElement[]) : []),
		...(includeStars ? (['stars'] as LayoutElement[]) : []),
		'author',
	];

	const sizableElement = selectedElement === 'text' || selectedElement === 'author' ? selectedElement : null;

	const commitLayout = (next: Api.IGoogleReviewPostLayout) => {
		if (!template) {
			return;
		}
		setValue('layout', next, { shouldDirty: true });
		setValue('templateKey', template.key, { shouldDirty: true });
		setValue('templateRevision', template.revision, { shouldDirty: true });
	};

	const handleLayoutBoxChange = (element: LayoutElement, box: Api.IGoogleReviewPostLayoutBox) => {
		if (resolvedLayout) {
			commitLayout({ ...resolvedLayout, [element]: box });
		}
	};

	const handleFontSizeChange = (fontSize: number) => {
		if (resolvedLayout && sizableElement) {
			commitLayout({ ...resolvedLayout, [sizableElement]: { ...resolvedLayout[sizableElement], fontSize } });
		}
	};

	const { mutateAsync: uploadCroppedImage } = useImageAttachmentMutation({
		onError: () => {
			toast.error('Failed to upload cropped image. Please try again.');
		},
	});

	const openCropModal = (dataUrl: string) => {
		setPendingCropImageUrl(dataUrl);
		setCropModalOpen(true);
	};

	const resolveAndOpenCrop = async (attachment: Api.IFileAttachment) => {
		try {
			const dataUrl = await getImageUrl(attachment);
			openCropModal(dataUrl);
		} catch {
			toast.error('Failed to load image. Please try again.');
		}
	};

	const handleImageSelectedFromDialog = (img: Api.IFileAttachment) => {
		setBackgroundImageModalOpen(false);
		if (img.url || img.id) {
			resolveAndOpenCrop(img);
		} else {
			setValue('image', img, { shouldDirty: true });
		}
	};

	const handleSelectImagesFromDialog = (images: { slug: string; fileAttachment: Api.IFileAttachment }[]) => {
		setBackgroundImageModalOpen(false);
		const selected = images[0];
		if (selected?.fileAttachment?.url || selected?.fileAttachment?.id) {
			resolveAndOpenCrop(selected.fileAttachment);
		} else if (selected) {
			setValue('image', selected.fileAttachment, { shouldDirty: true });
		}
	};

	const handleCropComplete = async (croppedFile: File) => {
		const croppedFrom = pendingCropImageUrl;
		const toastId = toast.loading('Uploading cropped image...');
		try {
			const attachments = await uploadCroppedImage({
				files: [croppedFile],
				resizeForSocialMedia: true,
				impersonationContext,
			});
			const uploaded = attachments[0];
			setValue('image', uploaded, { shouldDirty: true });
			setCropSource(croppedFrom && uploaded?.id ? { imageId: uploaded.id, url: croppedFrom } : null);
			toast.success('Background image applied!', { id: toastId });
		} catch {
			toast.error('Failed to upload cropped image.', { id: toastId });
		}
	};

	const handleEditCrop = () => {
		if (sourceImageUrl) {
			openCropModal(sourceImageUrl);
		} else if (image) {
			resolveAndOpenCrop(image);
		} else {
			setBackgroundImageModalOpen(true);
		}
	};

	const storedBranding = accountData?.preferences?.googleReviewPostBranding;
	const resetToStored = React.useCallback(() => {
		reset(getDefaultFormValues(storedBranding));
		setPickedFormat(null);
	}, [storedBranding, reset]);

	React.useEffect(() => resetToStored(), [resetToStored]);

	const brandingMutation = useSetGoogleBusinessBrandingSettings({
		impersonationContext,
		onError: err => {
			errorMessages.pushApiError(err);
		},
		onSuccess: () => {
			reset(getValues());
			invalidateAccountLoad();
			toast.success('Google Review branding settings saved successfully');
		},
	});

	const layoutBelongsToActiveFormat =
		templateIsForActiveFormat && templateKey === template?.key && templateRevision === template?.revision;

	const onValid = (data: GoogleReviewBrandingFormValues) => {
		brandingMutation.mutate({
			backgroundColor: data.backgroundColor,
			foregroundColor: data.foregroundColor,
			includeGoogleLogo: data.includeGoogleLogo,
			includeStars: data.includeStars,
			image: data.image,
			templateKey: activeFormatKey ?? data.templateKey,
			templateRevision: layoutBelongsToActiveFormat ? data.templateRevision : undefined,
			layout: layoutBelongsToActiveFormat ? data.layout : undefined,
		} as Api.IGoogleBusinessBrandingSettings);
	};

	const handleCancel = () => resetToStored();

	return (
		<div className='settings-admin-company-container'>
			<div className={css(styleSheet.brandingColumns)}>
				<div className={css(styleSheet.brandingPreferencesColumn)}>
					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)}>Colors</div>
						<div className={css(styleSheet.brandingColorsRow)}>
							<div className={css(styleSheet.brandingColorItem)}>
								<span className={css(styleSheet.brandingColorLabel)}>Background Color</span>
								<ColorPicker
									value={backgroundColor}
									onChange={c => setValue('backgroundColor', c, { shouldDirty: true })}
								/>
							</div>
							<div className={css(styleSheet.brandingColorItem)}>
								<span className={css(styleSheet.brandingColorLabel)}>Text Color</span>
								<ColorPicker
									value={foregroundColor}
									onChange={c => setValue('foregroundColor', c, { shouldDirty: true })}
								/>
							</div>
						</div>
					</div>

					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)}>Default Toggles</div>
						<div className={css(styleSheet.brandingCustomColorsList)}>
							<label
								className={css(
									baseStyleSheet.flex,
									baseStyleSheet.itemsCenter,
									baseStyleSheet.gap2,
									styleSheet.checkboxLabel
								)}
							>
								<Checkbox
									checked={includeGoogleLogo}
									onCheckedChange={checked => setValue('includeGoogleLogo', checked === true, { shouldDirty: true })}
								/>
								<span>Include Google Logo</span>
							</label>
							<label
								className={css(
									baseStyleSheet.flex,
									baseStyleSheet.itemsCenter,
									baseStyleSheet.gap2,
									styleSheet.checkboxLabel
								)}
							>
								<Checkbox
									checked={includeStars}
									onCheckedChange={checked => setValue('includeStars', checked === true, { shouldDirty: true })}
								/>
								<span>Include Stars</span>
							</label>
						</div>
					</div>
				</div>

				<div className={css(styleSheet.brandingLayoutColumn)}>
					{!!formats?.length && !!activeFormatKey && (
						<>
							<TabToggle
								aria-label='Testimonial card size'
								items={formats.map(format => ({ label: shapeOf(format.canvas), value: format.key }))}
								value={activeFormatKey}
								onValueChange={key => setPickedFormat({ accountId: account?.id, key })}
							/>
							<span className={css(styleSheet.brandingLayoutHint)}>
								The background and the box positions below belong to this size. An account uses one size at a time —
								whichever you set up here is what new testimonials are made at.
							</span>
						</>
					)}

					<div className={css(styleSheet.brandingSubsectionTitle)}>Background Image</div>
					<div className={css(styleSheet.brandingImageFieldGroup, styleSheet.brandingAddImageButton)}>
						{!image ? (
							<Button variant='secondary' size='sm' onClick={() => setBackgroundImageModalOpen(true)}>
								+ Add Background Image
							</Button>
						) : (
							<div className={css(styleSheet.brandingImageViewMode)}>
								<div className={css(styleSheet.brandingImageViewContent)}>
									{(image?.url ?? imageData?.url) && (
										<img
											src={image?.url ?? imageData?.url}
											alt='Default background'
											className={css(styleSheet.brandingBackgroundThumb)}
										/>
									)}
								</div>
								<div className={css(styleSheet.buttonContainer)}>
									{canRecropForThisFormat && (
										<Button variant='secondary' size='sm' onClick={handleEditCrop}>
											Crop
										</Button>
									)}
									<Button variant='secondary' size='sm' onClick={() => setBackgroundImageModalOpen(true)}>
										Change
									</Button>
									<Button
										variant='destructive-secondary'
										size='sm'
										onClick={() => {
											setValue('image', undefined, { shouldDirty: true });
											setCropSource(null);
										}}
									>
										Remove
									</Button>
								</div>
							</div>
						)}
					</div>
					{cropIsForAnotherFormat && !canRecropForThisFormat && (
						<span className={css(styleSheet.brandingBlockedHint)}>
							This background was cut for the {storedFormatShape ? shapeOf(storedFormatShape).toLowerCase() : 'other'}{' '}
							card, so it cannot be re-cropped to this one without blowing it up. Pick the image again to cut it for
							this size.
						</span>
					)}

					<div className={css(styleSheet.brandingLayoutCanvas)}>
						<TestimonialCardCanvas
							canvas={canvas}
							previewHtml={previewHtml}
							previewError={previewError}
							layout={resolvedLayout ?? undefined}
							visibleElements={visibleElements}
							selectedElement={selectedElement}
							onSelectElement={setSelectedElement}
							onLayoutBoxChange={handleLayoutBoxChange}
						/>
					</div>
					<div className={css(styleSheet.brandingLayoutControls)}>
						<FontSizeInput
							label={sizableElement === 'author' ? 'Author Font Size' : 'Quote Font Size'}
							value={sizableElement ? resolvedLayout?.[sizableElement]?.fontSize : undefined}
							onChange={handleFontSizeChange}
						/>
						<Button
							variant='link'
							size='sm'
							disabled={!template}
							onClick={() => template && commitLayout(template.defaultLayout)}
						>
							Reset to template default
						</Button>
					</div>
				</div>
			</div>

			<div className={css(styleSheet.brandingSaveRow, baseStyleSheet.mt3)}>
				<Button
					variant='primary'
					size='sm'
					onClick={handleSubmit(onValid)}
					disabled={
						!hasUnsavedChanges || brandingMutation.isPending || cropIsForAnotherFormat || !templateIsForActiveFormat
					}
				>
					{brandingMutation.isPending ? 'Saving...' : 'Save'}
				</Button>
				<Button variant='secondary' size='sm' onClick={handleCancel} disabled={!hasUnsavedChanges}>
					Cancel
				</Button>
				{cropIsForAnotherFormat && (
					<span className={css(styleSheet.brandingBlockedHint)}>Give this size its own background before saving.</span>
				)}
			</div>

			<UnifiedImageDialog
				showFreeImagesTab={true}
				showUploadTab={true}
				impersonationContext={impersonationContext}
				resizeForSocialMedia
				initialImage={backgroundAttachment}
				allowAIEdit={canEditBackgroundWithAI}
				initialTab={canEditBackgroundWithAI ? 'ai-edit' : 'select-image'}
				open={backgroundImageModalOpen}
				onOpenChange={setBackgroundImageModalOpen}
				onImageSelected={handleImageSelectedFromDialog}
				showSelectImageTab
				onSelectImages={handleSelectImagesFromDialog}
				allowMultipleSelect={false}
				imagesForSelection={IMAGES_FOR_SELECTION}
				selectImageTitle='Select a Background Image'
				selectImageSubtitle='Note: Images will have an overlayed color to help with readability. You can change this by editing with AI or applying your brand to the selected image.'
				onPreProcessSelectImage={async file =>
					addColorOverlayToImage(file, TESTIMONIAL_OVERLAY_COLOR, TESTIMONIAL_OVERLAY_OPACITY)
				}
			/>

			{pendingCropImageUrl && canvas && (
				<ImageCropDialog
					fileName='google-review-background.png'
					imageUrl={pendingCropImageUrl}
					lockedAspect={canvas.width / canvas.height}
					open={cropModalOpen}
					onOpenChange={open => {
						setCropModalOpen(open);
						if (!open) {
							setPendingCropImageUrl(null);
						}
					}}
					onCropComplete={handleCropComplete}
					outputSize={canvas}
					title='Adjust Background Image'
				/>
			)}
		</div>
	);
};
